import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { supabase } from "../../supabaseClient"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { Button } from "../../components/ui/button"
import { formatPlace } from "../../utils/scoring"
import { getActiveYear } from "../../utils/activeYear"

interface PayoutRow {
  id?: number
  year: number
  place: number
  percentage: number
}

export default function PayoutConfig() {
  const [payouts, setPayouts] = useState<PayoutRow[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [paidCount, setPaidCount] = useState(0)
  const [activeYear, setActiveYearState] = useState<number | null>(null)
  
  // Entry fee state
  const [entryFee, setEntryFee] = useState<number>(20)
  const [editingFee, setEditingFee] = useState(false)
  const [tempFee, setTempFee] = useState<number>(20)

  // Payment link state
  const [paymentLink, setPaymentLink] = useState<string>('https://venmo.com/u/bfwright')
  const [editingPaymentLink, setEditingPaymentLink] = useState(false)
  const [tempPaymentLink, setTempPaymentLink] = useState<string>('https://venmo.com/u/bfwright')
  
  // Champion game state
  const [championEntryFee, setChampionEntryFee] = useState<number>(5)
  const [editingChampionFee, setEditingChampionFee] = useState(false)
  const [tempChampionFee, setTempChampionFee] = useState<number>(5)
  const [championGameOpen, setChampionGameOpen] = useState(false)
  const [togglingChampionGame, setTogglingChampionGame] = useState(false)
  const [championEntriesCount, setChampionEntriesCount] = useState(0)
  const [championPaidCount, setChampionPaidCount] = useState(0)

  useEffect(() => {
    loadActiveYear()
  }, [])

  async function loadActiveYear() {
    const year = await getActiveYear()
    setActiveYearState(year)
    loadData(year)
  }

  async function loadData(year: number) {
    // Get year data for entry fee, payment link, and champion game config
    const { data: yearData } = await supabase
      .from('years')
      .select('entry_fee, payment_link, champion_entry_fee, champion_game_open')
      .eq('year', year)
      .single()

    if (yearData) {
      setEntryFee(yearData.entry_fee || 20)
      setTempFee(yearData.entry_fee || 20)
      const link = yearData.payment_link || 'https://venmo.com/u/bfwright'
      setPaymentLink(link)
      setTempPaymentLink(link)
      setChampionEntryFee(yearData.champion_entry_fee || 5)
      setTempChampionFee(yearData.champion_entry_fee || 5)
      setChampionGameOpen(yearData.champion_game_open || false)
    }
    
    // Get champion entries count - total
    const { count: championTotalCount, error: totalError } = await supabase
      .from('champion_entries')
      .select('*', { count: 'exact', head: true })
      .eq('year', year)
    
    // Get champion entries with their main entry payment status
    // Champion entries are considered paid if their corresponding main entry is paid
    const { data: championEntriesData, error: championError } = await supabase
      .from('champion_entries')
      .select(`
        entry_id,
        is_paid,
        entry:entries!champion_entries_entry_id_fkey(is_paid)
      `)
      .eq('year', year)
    
    // Count champion entries where main entry is paid OR champion entry is explicitly marked as paid
    const paidChampionEntries = (championEntriesData || []).filter((ce: any) => {
      // Handle nested entry data (could be array or object)
      const entry = Array.isArray(ce.entry) ? ce.entry[0] : ce.entry
      const mainEntryPaid = entry?.is_paid === true
      const championExplicitlyPaid = ce.is_paid === true
      return mainEntryPaid || championExplicitlyPaid
    }).length
    
    // Debug logging
    console.log('[PayoutConfig] Champion entries:', {
      total: championTotalCount,
      paid: paidChampionEntries,
      totalError,
      championError,
      year,
      sampleData: championEntriesData?.slice(0, 2)
    })
    
    setChampionEntriesCount(championTotalCount || 0)
    setChampionPaidCount(paidChampionEntries)

    // Get payout config
    const { data: payoutData } = await supabase
      .from('payout_config')
      .select('*')
      .eq('year', year)
      .order('place', { ascending: true })

    // Get paid entries count
    const { count } = await supabase
      .from('entries')
      .select('*', { count: 'exact', head: true })
      .eq('year', year)
      .eq('is_paid', true)

    // Ensure percentages are parsed as integers
    const parsedPayouts = (payoutData || []).map((p: any) => ({
      ...p,
      percentage: Math.round(Number(p.percentage))
    }))
    setPayouts(parsedPayouts)
    setPaidCount(count || 0)
    setLoading(false)
  }

  async function handleSaveEntryFee() {
    if (tempFee < 0) return
    
    await supabase
      .from('years')
      .update({ entry_fee: tempFee })
      .eq('year', activeYear)
    
    setEntryFee(tempFee)
    setEditingFee(false)
  }

  async function handleSavePaymentLink() {
    if (!tempPaymentLink.trim()) return
    
    await supabase
      .from('years')
      .update({ payment_link: tempPaymentLink.trim() })
      .eq('year', activeYear)
    
    setPaymentLink(tempPaymentLink.trim())
    setEditingPaymentLink(false)
  }
  
  async function handleSaveChampionFee() {
    if (tempChampionFee < 0) return
    
    await supabase
      .from('years')
      .update({ champion_entry_fee: tempChampionFee })
      .eq('year', activeYear)
    
    setChampionEntryFee(tempChampionFee)
    setEditingChampionFee(false)
  }
  
  async function handleToggleChampionGame() {
    if (!activeYear) return
    setTogglingChampionGame(true)
    
    const { error } = await supabase
      .from('years')
      .update({ champion_game_open: !championGameOpen })
      .eq('year', activeYear)
    
    if (!error) {
      setChampionGameOpen(!championGameOpen)
    }
    setTogglingChampionGame(false)
  }

  const totalPot = paidCount * entryFee
  const totalPercentage = payouts.reduce((sum, p) => sum + p.percentage, 0)

  function updatePercentage(place: number, value: string) {
    const percentage = parseInt(value) || 0
    setPayouts(payouts.map(p => 
      p.place === place ? { ...p, percentage } : p
    ))
  }

  function addPlace() {
    if (!activeYear) return
    const nextPlace = payouts.length + 1
    setPayouts([...payouts, { year: activeYear, place: nextPlace, percentage: 0 }])
  }

  function removePlace(place: number) {
    setPayouts(payouts.filter(p => p.place !== place).map((p, i) => ({
      ...p,
      place: i + 1
    })))
  }

  async function savePayouts() {
    if (!activeYear) return
    setSaving(true)
    
    // Delete existing
    const { error: deleteError } = await supabase
      .from('payout_config')
      .delete()
      .eq('year', activeYear)
    
    if (deleteError) {
      console.error('Delete error:', deleteError)
      alert('Error deleting old payouts: ' + deleteError.message)
      setSaving(false)
      return
    }

    // Insert new - ensure year is set correctly for all payouts
    const payoutsToInsert = payouts.map(p => ({
      year: activeYear,
      place: p.place,
      percentage: Math.round(p.percentage) // Ensure whole numbers
    }))

    console.log('Inserting payouts:', payoutsToInsert)

    const { error: insertError } = await supabase
      .from('payout_config')
      .insert(payoutsToInsert)

    if (insertError) {
      console.error('Insert error:', insertError)
      alert('Error saving payouts: ' + insertError.message)
    } else {
      alert('Payouts saved successfully!')
      // Reload to confirm
      loadData(activeYear)
    }
    
    setSaving(false)
  }

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh] text-gray-500">Loading...</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin" className="text-gray-400 hover:text-gray-600">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Payout Configuration</h1>
          <p className="text-gray-500">Set entry fee and prize distribution for <span className="font-semibold text-[#F15A22]">{activeYear}</span></p>
        </div>
      </div>

      {/* Entry Fee - Standalone editable */}
      <Card className="bg-white border-0 shadow-sm">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500">Entry Fee</div>
              {editingFee ? (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-lg font-bold text-[#1e3a5f]">$</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={String(tempFee)}
                    onChange={(e) => setTempFee(parseInt(e.target.value.replace(/^0+/, '') || '0') || 0)}
                    className="w-20 px-2 py-1 rounded-lg border border-gray-200 text-center text-lg font-bold text-[#1e3a5f]"
                    autoFocus
                  />
                  <button
                    onClick={handleSaveEntryFee}
                    className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => { setEditingFee(false); setTempFee(entryFee) }}
                    className="px-2 py-1 text-gray-500 hover:bg-gray-100 rounded-lg text-sm"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setEditingFee(true)}
                  className="text-2xl font-bold text-[#1e3a5f] hover:text-[#F15A22]"
                >
                  ${entryFee.toLocaleString()}
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pool Stats - Simple row */}
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <div><span className="font-bold text-[#1e3a5f]">{paidCount}</span> <span className="text-gray-500">paid</span></div>
        <div><span className="font-bold text-green-600">${totalPot.toLocaleString()}</span> <span className="text-gray-500">pot</span></div>
        <div>
          <span className={`font-bold ${totalPercentage !== 100 ? 'text-red-500' : 'text-[#1e3a5f]'}`}>{totalPercentage}%</span>
          <span className="text-gray-500"> allocated</span>
        </div>
      </div>

      {/* Payment Link Settings */}
      <Card className="bg-white border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#1e3a5f]">Payment Link</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-2">
            <p className="text-sm text-gray-500">
              This URL is used for the "Pay" button on unpaid entries. Include your Venmo username.
            </p>
            {editingPaymentLink ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="url"
                  value={tempPaymentLink}
                  onChange={(e) => setTempPaymentLink(e.target.value)}
                  placeholder="https://venmo.com/u/yourusername"
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleSavePaymentLink}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    Save
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setEditingPaymentLink(false); setTempPaymentLink(paymentLink) }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-50 rounded-lg text-sm text-gray-700 break-all">
                  {paymentLink}
                </code>
                <Button
                  variant="outline"
                  onClick={() => setEditingPaymentLink(true)}
                  className="shrink-0"
                >
                  Edit
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Champion Side Pot Config */}
      <Card className="bg-white border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#1e3a5f]">Champion Side Pot</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Champion Game Open Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-[#1e3a5f]">Side Pot Open</div>
              <div className="text-sm text-gray-500">
                {championGameOpen ? 'Users can opt into champion contest' : 'Champion contest disabled'}
              </div>
            </div>
            <button
              onClick={handleToggleChampionGame}
              disabled={togglingChampionGame}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors shrink-0 ${
                championGameOpen ? 'bg-green-500' : 'bg-gray-300'
              } ${togglingChampionGame ? 'opacity-50' : ''}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                  championGameOpen ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          
          {/* Champion Entry Fee */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div>
              <div className="text-sm text-gray-500">Side Pot Entry Fee</div>
              {editingChampionFee ? (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-lg font-bold text-[#1e3a5f]">$</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={String(tempChampionFee)}
                    onChange={(e) => setTempChampionFee(parseInt(e.target.value.replace(/^0+/, '') || '0') || 0)}
                    className="w-20 px-2 py-1 rounded-lg border border-gray-200 text-center text-lg font-bold text-[#1e3a5f]"
                    autoFocus
                  />
                  <button
                    onClick={handleSaveChampionFee}
                    className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => { setEditingChampionFee(false); setTempChampionFee(championEntryFee) }}
                    className="px-2 py-1 text-gray-500 hover:bg-gray-100 rounded-lg text-sm"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setEditingChampionFee(true)}
                  className="text-2xl font-bold text-[#1e3a5f] hover:text-[#F15A22]"
                >
                  ${championEntryFee.toLocaleString()}
                </button>
              )}
            </div>
          </div>
          
          {/* Champion Pot Stats */}
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm pt-2 border-t border-gray-100">
            <div><span className="font-bold text-[#1e3a5f]">{championEntriesCount}</span> <span className="text-gray-500">entries</span></div>
            <div><span className="font-bold text-[#1e3a5f]">{championPaidCount}</span> <span className="text-gray-500">paid</span></div>
            <div><span className="font-bold text-green-600">${((championPaidCount || 0) * (championEntryFee || 0)).toLocaleString()}</span> <span className="text-gray-500">pot</span></div>
          </div>
        </CardContent>
      </Card>

      {/* Payout Editor */}
      <Card className="bg-white border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#1e3a5f]">Prize Distribution</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {payouts.map((payout) => (
            <div key={payout.place} className="flex items-center gap-4">
              <div className="w-16 font-medium text-[#1e3a5f]">
                {formatPlace(payout.place)}
              </div>
              <div className="flex-1 flex items-center gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={String(Math.round(payout.percentage))}
                  onChange={(e) => updatePercentage(payout.place, e.target.value.replace(/^0+/, '') || '0')}
                  className="w-20 px-3 py-2 border border-gray-200 rounded-lg text-center"
                />
                <span className="text-gray-500">%</span>
              </div>
              <div className="w-24 text-right font-medium text-green-600">
                ${Math.round((payout.percentage / 100) * totalPot).toLocaleString()}
              </div>
              <button
                onClick={() => removePlace(payout.place)}
                className="text-gray-400 hover:text-red-500"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          ))}

          <div className="flex gap-4 pt-4 border-t border-gray-100">
            <Button variant="outline" onClick={addPlace}>
              + Add Place
            </Button>
            <Button 
              onClick={savePayouts} 
              disabled={saving || totalPercentage !== 100}
              className="bg-[#1e3a5f] hover:bg-[#002a6b] text-white"
            >
              {saving ? 'Saving...' : 'Save Payouts'}
            </Button>
            {totalPercentage !== 100 && (
              <span className="text-red-500 text-sm self-center">
                Total must equal 100%
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
